<template>
  <div class="max-w-5xl mx-auto">
    <Head title="Gestión de Inventario" />

    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center gap-4 text-sm text-gray-500 mb-2">
        <button class="hover:text-orange-500 transition-colors" @click="back">
          <i class="fa fa-arrow-left" />
        </button>
        <Link href="/inventories" class="hover:text-orange-500 transition-colors">Inventario</Link>
        <span class="text-gray-300">/</span>
        <span class="text-gray-900 font-medium">Entrada</span>
      </div>
      
      <h1 class="text-2xl font-bold text-gray-900">Gestión de Inventario</h1>
      <p class="text-gray-500 mt-1">Busque productos y registre entradas de inventario en los depósitos correspondientes.</p>
    </div>

    <!-- Buscador de productos -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
      <div class="p-6 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <i class="fa fa-search text-orange-500" />
            Búsqueda de Productos
          </h3>
          <p class="text-xs text-gray-400 mt-0.5">Seleccione los productos existentes o cree uno nuevo rápidamente</p>
        </div>

        <button 
          type="button"
          class="px-3.5 py-2 bg-orange-50 hover:bg-orange-100 text-orange-600 border border-orange-200 rounded-lg flex items-center gap-2 transition-colors font-medium text-sm shadow-sm" 
          @click="openQuickProductModal"
        >
          <i class="fa fa-plus-circle text-orange-500" />
          <span>Crear producto</span>
        </button>
      </div>

      <div class="p-6 pt-4 border-b border-gray-100">
        <div class="relative w-full lg:w-1/2">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i class="fa fa-search text-gray-400" />
          </div>
          <input 
            v-model="form.search.terms"
            type="text" 
            class="block w-full pl-10 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
            placeholder="Buscar por nombre, código o descripción..."
          />
        </div>
      </div>

      <!-- Tabla de resultados de búsqueda -->
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap text-sm text-left">
          <thead>
            <tr class="bg-gray-50/50 text-gray-500 border-b border-gray-100">
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Código</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Producto</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Disponibles</th>
              <th class="py-3 px-6" />
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="product in products.data" 
              :key="product.id"
              class="hover:bg-orange-50/30 cursor-pointer transition-colors group"
              @click="addProduct(product)"
            >
              <td class="py-3 px-6 text-gray-900 font-medium font-mono">{{ product?.code }}</td>
              <td class="py-3 px-6 text-gray-700">{{ product?.name }}</td>
              <td class="py-3 px-6 text-gray-700">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {{ product?.quantity }}
                </span>
              </td>
              <td class="py-3 px-6 text-right">
                <button class="text-orange-500 hover:text-orange-700 transition-colors opacity-0 group-hover:opacity-100">
                  <i class="fa fa-plus-circle text-lg" />
                </button>
              </td>
            </tr>
            <tr v-if="!products?.data?.length">
              <td colspan="4" class="py-8 text-center text-gray-400">
                <div class="flex flex-col items-center justify-center">
                  <i class="fa fa-box-open text-3xl mb-2 text-gray-300" />
                  <p class="mb-2">No se encontraron productos</p>
                  <button 
                    type="button"
                    class="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors shadow-sm"
                    @click="openQuickProductModal"
                  >
                    <i class="fa fa-plus" />
                    <span>Crear producto</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Lista de productos seleccionados -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="p-6 border-b border-gray-100">
        <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <i class="fa fa-clipboard-list text-orange-500" />
          Productos Seleccionados
        </h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap text-sm text-left">
          <thead>
            <tr class="bg-gray-50/50 text-gray-500 border-b border-gray-100">
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs w-32">Cantidad</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs w-48">Depósito</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Código</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Producto</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Stock Actual</th>
              <th class="py-3 px-6 w-16" />
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="product in items" :key="product.id" class="hover:bg-gray-50 transition-colors">
              <td class="py-3 px-6">
                <input
                  v-model.number="quantities.qtys[product.id]"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="block w-24 px-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 text-sm shadow-sm transition-colors"
                />
              </td>
              <td class="py-3 px-6">
                <div class="relative">
                  <select 
                    v-model="quantities.deposits[product.id]"
                    class="block w-48 pl-3 pr-8 py-2 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 text-sm shadow-sm appearance-none transition-colors"
                  >
                    <option 
                      v-for="deposit in deposits" 
                      :key="deposit.id"
                      :value="deposit.name"
                    >
                      {{ deposit.name }}
                    </option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <i class="fa fa-chevron-down text-gray-400 text-xs" />
                  </div>
                </div>
              </td>
              <td class="py-3 px-6 font-mono text-gray-600">{{ product.code }}</td>
              <td class="py-3 px-6 font-medium text-gray-900">{{ product.name }}</td>
              <td class="py-3 px-6 text-gray-500">{{ product.quantity }}</td>
              <td class="py-3 px-6 text-right">
                <button 
                  class="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                  title="Eliminar producto"
                  @click="removeProduct(product)"
                >
                  <i class="fa fa-trash" />
                </button>
              </td>
            </tr>
            <tr v-if="!items.length">
              <td colspan="6" class="py-8 text-center text-gray-400">
                <div class="flex flex-col items-center justify-center">
                  <i class="fa fa-shopping-basket text-3xl mb-2 text-gray-200" />
                  <p>Seleccione productos de la búsqueda para agregarlos aquí</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
        <loading-button 
          :loading="quantities.processing" 
          class="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg shadow-sm transition-colors flex items-center gap-2"
          @click="generate"
        >
          <i class="fa fa-save" />
          Guardar Entrada de Productos
        </loading-button>
      </div>
    </div>

    <!-- Modal Crear Producto -->
    <div v-if="showQuickProductModal" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
      <div class="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 border border-gray-100">
        <div class="flex justify-between items-center mb-5 pb-3 border-b border-gray-100">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i class="fa fa-box-open text-orange-500" />
            Crear producto
          </h3>
          <button class="text-gray-400 hover:text-gray-600 transition-colors" @click="showQuickProductModal = false">
            <i class="fa fa-times" />
          </button>
        </div>

        <form class="space-y-4" @submit.prevent="saveQuickProduct">
          <!-- Nombre -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Nombre del Producto <span class="text-red-500">*</span></label>
            <input 
              v-model="quickForm.name" 
              type="text" 
              required 
              placeholder="Ej: Filtro de Aceite PH-8A"
              class="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              :class="productErrors.name ? 'border-red-500' : 'border-gray-200'"
            />
            <p v-if="productErrors.name" class="text-xs text-red-500 mt-1">{{ productErrors.name[0] }}</p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Código -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Código / Referencia</label>
              <input 
                v-model="quickForm.code" 
                type="text" 
                placeholder="Autogenerado si está vacío"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              />
            </div>

            <!-- Categoría -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Categoría</label>
              <select 
                v-model="quickForm.product_category_id"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white"
              >
                <option value="">Sin categoría</option>
                <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Costo -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Costo Unitario ($)</label>
              <input 
                v-model="quickForm.cost" 
                type="number" 
                step="0.01" 
                min="0"
                placeholder="0.00"
                class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              />
            </div>

            <!-- Precio de Venta -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Precio Venta ($) <span class="text-red-500">*</span></label>
              <input 
                v-model="quickForm.price" 
                type="number" 
                step="0.01" 
                min="0"
                required
                placeholder="0.00"
                class="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                :class="productErrors.price ? 'border-red-500' : 'border-gray-200'"
              />
              <p v-if="productErrors.price" class="text-xs text-red-500 mt-1">{{ productErrors.price[0] }}</p>
            </div>
          </div>

          <!-- Marca -->
          <div v-if="brands && brands.length">
            <label class="block text-xs font-semibold text-gray-700 uppercase mb-1">Marca</label>
            <select 
              v-model="quickForm.brand_id"
              class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white"
            >
              <option value="">Genérico / Por defecto</option>
              <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
          </div>

          <div class="flex justify-end items-center gap-3 pt-4 border-t border-gray-100">
            <button 
              type="button" 
              class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              @click="showQuickProductModal = false"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              :disabled="isSavingProduct"
              class="px-5 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <i v-if="isSavingProduct" class="fa fa-spinner fa-spin" />
              <i v-else class="fa fa-check" />
              <span>Guardar y Agregar a Inventario</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import pickBy from 'lodash/pickBy'
import debounce from 'lodash/debounce'
import Swal from 'sweetalert2'
import axios from 'axios'

export default {
  name: 'InventoriesCreate',

  components: {
    Head,
    Link,
    LoadingButton,
  },
  
  layout: Layout,
  
  props: {
    products: Object,
    deposits: Array,
    brands: Array,
    categories: Array,
  },

  data() {
    return {
      quantities: this.$inertia.form({
        qtys: {},
        products: [],
        deposits: {},
      }),

      form: this.$inertia.form({
        search: {
          terms: '',
          deposit: '',
          brand_id: '',
          part_id: '',
          truck_model_id: '',
          region_id: '',
        },
      }),

      items: [],
      showQuickProductModal: false,
      isSavingProduct: false,
      productErrors: {},
      quickForm: {
        name: '',
        code: '',
        product_category_id: '',
        brand_id: '',
        cost: '',
        price: '',
      },
    }
  },

  watch: {
    'form.search': {
      handler: debounce(function() {
        this.searchProducts()
      }, 300),
      deep: true,
    },
  },

  methods: {
    back() {
      window.history.back()
    },

    searchProducts() {
      this.$inertia.get('/inventories/create', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        },
      )
    },

    openQuickProductModal() {
      this.quickForm = {
        name: this.form.search.terms || '',
        code: '',
        product_category_id: '',
        brand_id: '',
        cost: '',
        price: '',
      }
      this.productErrors = {}
      this.showQuickProductModal = true
    },

    async saveQuickProduct() {
      this.isSavingProduct = true
      this.productErrors = {}

      try {
        const response = await axios.post('/inventories/quick-product', this.quickForm)
        if (response.data?.success && response.data?.product) {
          const newProd = response.data.product
          this.addProduct(newProd)
          this.showQuickProductModal = false
          this.quickForm = {
            name: '',
            code: '',
            product_category_id: '',
            brand_id: '',
            cost: '',
            price: '',
          }
          this.form.search.terms = ''
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          this.productErrors = error.response.data.errors
        } else {
          alert('Error al crear el producto: ' + (error.response?.data?.message || error.message))
        }
      } finally {
        this.isSavingProduct = false
      }
    },

    addProduct(product) {
      if (!this.items.find(item => item.id === product.id)) {
        this.items.push(product)
        const defaultDeposit = (this.deposits && this.deposits.length) ? this.deposits[0].name : ''
        this.quantities.qtys[product.id] = 1
        this.quantities.deposits[product.id] = defaultDeposit
      }
    },

    removeProduct(product) {
      this.items = this.items.filter(item => item.id !== product.id)
      delete this.quantities.qtys[product.id]
      delete this.quantities.deposits[product.id]
    },

    generate() {
      if (!this.items.length) {
        Swal.fire({
          icon: 'warning',
          title: 'Sin productos',
          text: 'Debe agregar al menos un producto a la lista',
          confirmButtonColor: '#f97316',
        })
        return
      }

      // Validar que cada producto tenga un depósito asignado
      for (const item of this.items) {
        if (!this.quantities.deposits[item.id]) {
          Swal.fire({
            icon: 'warning',
            title: 'Depósito Requerido',
            text: `Por favor seleccione el depósito donde se guardará "${item.name}"`,
            confirmButtonColor: '#f97316',
          })
          return
        }
      }

      // Generar resumen visual de dónde se guardará cada producto
      let summaryHtml = '<div class="text-left text-sm mt-3"><p class="font-semibold text-gray-700 mb-2">Se registrará la entrada en los siguientes depósitos:</p><ul class="space-y-1.5 bg-gray-50 p-3 rounded-lg border border-gray-100 max-h-48 overflow-y-auto">'
      this.items.forEach(item => {
        const qty = this.quantities.qtys[item.id] || 1
        const dep = this.quantities.deposits[item.id] || 'Depósito Principal'
        summaryHtml += `<li class="flex justify-between items-center text-xs py-1 border-b border-gray-100 last:border-0"><span class="text-gray-800 font-medium">${item.name} (${qty} uds)</span> <span class="bg-orange-100 text-orange-800 px-2 py-0.5 rounded text-xs font-semibold"><i class="fa fa-warehouse mr-1"></i>${dep}</span></li>`
      })
      summaryHtml += '</ul><p class="text-xs text-gray-500 mt-2">¿Desea confirmar y guardar o prefiere revisar y escoger otro depósito?</p></div>'

      Swal.fire({
        title: 'Confirmar Entrada de Inventario',
        html: summaryHtml,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f97316',
        cancelButtonColor: '#6b7280',
        confirmButtonText: '<i class="fa fa-check mr-1"></i> Sí, Guardar Entrada',
        cancelButtonText: '<i class="fa fa-edit mr-1"></i> Cambiar / Escoger otro',
      }).then((result) => {
        if (result.isConfirmed) {
          this.quantities.products = this.items.map(item => item.id)
          this.quantities.post('/inventories')
        }
      })
    },
  },
}
</script>
